/** Charity avatar fallback colours and optional branding from the API. */

type Rgb = { r: number; g: number; b: number }

export type CharityAvatarColours = {
  background: string
  color: string
}

/** Balanced swatches, richer than pale pastels, softer than solid blocks. */
const PALETTE_SATURATION_MIN = 56
const PALETTE_SATURATION_RANGE = 14
const PALETTE_BACKGROUND_LIGHTNESS_MIN = 72
const PALETTE_BACKGROUND_LIGHTNESS_RANGE = 10
const PALETTE_FOREGROUND_LIGHTNESS_MIN = 26
const PALETTE_FOREGROUND_LIGHTNESS_RANGE = 6

const WHITE: Rgb = { r: 255, g: 255, b: 255 }
const DARK_TEXT = '#1c2330'
const DARK_LUMINANCE = relativeLuminance({ r: 28, g: 35, b: 48 })
const WHITE_LUMINANCE = 1
const MIN_CONTRAST_RATIO = 4.5

/** Normalise free-text charity names so spelling variants hash consistently. */
export function normaliseCharityName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase()
}

/** First letter for the avatar square (falls back to "?"). */
export function charityAvatarInitial(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  return trimmed.charAt(0).toUpperCase()
}

function hashCharityName(normalised: string): number {
  let hash = 0
  for (let i = 0; i < normalised.length; i += 1) {
    hash = (hash << 5) - hash + normalised.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const scaled = value / 255
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4
  }

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

function contrastRatio(lumA: number, lumB: number): number {
  const lighter = Math.max(lumA, lumB)
  const darker = Math.min(lumA, lumB)
  return (lighter + 0.05) / (darker + 0.05)
}

function rgbToHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const sNorm = s / 100
  const lNorm = l / 100
  const chroma = (1 - Math.abs(2 * lNorm - 1)) * sNorm
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = lNorm - chroma / 2

  let r = 0
  let g = 0
  let b = 0

  if (h < 60) {
    r = chroma
    g = x
  } else if (h < 120) {
    r = x
    g = chroma
  } else if (h < 180) {
    g = chroma
    b = x
  } else if (h < 240) {
    g = x
    b = chroma
  } else if (h < 300) {
    r = x
    b = chroma
  } else {
    r = chroma
    b = x
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  }
}

function parseHexColor(input: string): Rgb | null {
  const hex = input.trim().replace(/^#/, '')
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    }
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    }
  }
  return null
}

function mixRgb(a: Rgb, b: Rgb, weightA: number): Rgb {
  const weight = Math.min(1, Math.max(0, weightA))
  return {
    r: Math.round(a.r * weight + b.r * (1 - weight)),
    g: Math.round(a.g * weight + b.g * (1 - weight)),
    b: Math.round(a.b * weight + b.b * (1 - weight)),
  }
}

function darken(rgb: Rgb, amount: number): Rgb {
  const factor = 1 - Math.min(1, Math.max(0, amount))
  return {
    r: Math.round(rgb.r * factor),
    g: Math.round(rgb.g * factor),
    b: Math.round(rgb.b * factor),
  }
}

function pickForeground(background: Rgb, preferred: Rgb): string {
  const bgLuminance = relativeLuminance(background)
  const preferredLuminance = relativeLuminance(preferred)

  if (contrastRatio(preferredLuminance, bgLuminance) >= MIN_CONTRAST_RATIO) {
    return rgbToHex(preferred)
  }

  const whiteContrast = contrastRatio(WHITE_LUMINANCE, bgLuminance)
  const darkContrast = contrastRatio(bgLuminance, DARK_LUMINANCE)

  if (whiteContrast >= darkContrast && whiteContrast >= MIN_CONTRAST_RATIO) {
    return '#ffffff'
  }

  if (darkContrast >= MIN_CONTRAST_RATIO) {
    return DARK_TEXT
  }

  return rgbToHex(darken(preferred, 0.35))
}

function coloursFromBrandColor(brandColor: string): CharityAvatarColours | null {
  const brand = parseHexColor(brandColor)
  if (!brand) return null

  const background = mixRgb(brand, WHITE, 0.28)
  const foreground = pickForeground(background, darken(brand, 0.08))

  return {
    background: rgbToHex(background),
    color: foreground,
  }
}

function coloursFromPalette(name: string): CharityAvatarColours {
  const hash = hashCharityName(normaliseCharityName(name))
  const hue = hash % 360
  const saturation = PALETTE_SATURATION_MIN + (hash % PALETTE_SATURATION_RANGE)
  const backgroundLightness =
    PALETTE_BACKGROUND_LIGHTNESS_MIN + (hash % PALETTE_BACKGROUND_LIGHTNESS_RANGE)
  const foregroundLightness =
    PALETTE_FOREGROUND_LIGHTNESS_MIN + (hash % PALETTE_FOREGROUND_LIGHTNESS_RANGE)

  const background = hslToRgb(hue, saturation, backgroundLightness)
  const preferredForeground = hslToRgb(
    hue,
    Math.min(saturation + 8, 72),
    foregroundLightness
  )

  return {
    background: rgbToHex(background),
    color: pickForeground(background, preferredForeground),
  }
}

/**
 * Resolve letter-fallback colours: optional charity brand colour from the API,
 * otherwise a stable mid-tone swatch derived from the charity name.
 */
export function charityAvatarColours(
  name: string,
  brandColor?: string | null
): CharityAvatarColours {
  const trimmedBrand = brandColor?.trim()
  if (trimmedBrand) {
    const fromBrand = coloursFromBrandColor(trimmedBrand)
    if (fromBrand) return fromBrand
  }

  return coloursFromPalette(name)
}

export function charityAvatarStyle(
  name: string,
  brandColor?: string | null
): Record<string, string> {
  const { background, color } = charityAvatarColours(name, brandColor)

  return {
    backgroundColor: background,
    color,
  }
}

/** Map optional charity branding fields from API payloads (camelCase or snake_case). */
export function mapDonationCharityBranding(source: Record<string, unknown>): {
  charityLogoUrl?: string
  charityBrandColor?: string
} {
  const logoUrl = source.charityLogoUrl ?? source.charity_logo_url
  const brandColor = source.charityBrandColor ?? source.charity_brand_color

  return {
    charityLogoUrl:
      typeof logoUrl === 'string' && logoUrl.trim() ? logoUrl.trim() : undefined,
    charityBrandColor:
      typeof brandColor === 'string' && brandColor.trim() ? brandColor.trim() : undefined,
  }
}
